import { useState, useRef, useCallback, RefObject } from "react";

export interface UseDropdownOptions {
  disabled?: boolean;
}

export interface UseDropdownReturn<T extends HTMLElement> {
  isOpen: boolean;
  triggerRef: RefObject<T | null>;
  toggle: () => void;
  close: () => void;
}

/**
 * Hook for managing dropdown open/close state.
 * Pair with <DropdownMenu> for portal rendering, positioning, and click-outside.
 */
export function useDropdown<T extends HTMLElement = HTMLButtonElement>(
  options: UseDropdownOptions = {},
): UseDropdownReturn<T> {
  const { disabled = false } = options;

  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<T>(null);

  const toggle = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    triggerRef,
    toggle,
    close,
  };
}
